// Pure presentational helpers extracted from HoldingsList.tsx so the main
// component stays focused on render logic + state. Used only by Holdings
// surfaces — not exported from a top-level module.

import { compositeScore } from '@/lib/composite-score';
import { compositeTier as sharedTier } from '@/lib/composite-tiers';
import type { HoldingSelfPctile } from '@/lib/hooks/use-holdings-data';

export const INDEX_ETFS = new Set(['SPY', 'QQQ', 'IWM', 'DIA', 'VTI', 'VOO', 'IVV']);
export const TRIM_THRESHOLD = 0; // composite < 0 = "trim / underweight"

export function pctColor(p: number | null): string {
  if (p == null) return '#475569';
  if (p >= 90) return '#22c55e';
  if (p >= 70) return '#4ade80';
  if (p >= 30) return '#94a3b8';
  if (p >= 10) return '#fb923c';
  return '#f87171';
}

export function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

export function compositeFor(q: number | null | undefined, v: number | null | undefined): number | null {
  return compositeScore(q ?? null, v ?? null);
}

export function compTier(c: number | null): { label: string; color: string } {
  const t = sharedTier(c);
  return { label: t.label, color: t.textClass };
}

export function PortfolioCell({ wa }: { wa: { value: number | null; coverage: number } }) {
  if (wa.value == null) {
    return <div className="col-span-1 text-right text-[#475569]">—</div>;
  }
  const tierColor = compTier(wa.value).color;
  const opacity = wa.coverage < 0.8 ? 'opacity-60' : '';
  return (
    <div className={`col-span-1 text-right tabular-nums ${tierColor} ${opacity}`} title={`${(wa.coverage * 100).toFixed(0)}% of book has data`}>
      {wa.value.toFixed(2)}
    </div>
  );
}

/** Weighted-avg percentile cell for the PORTFOLIO totals row. */
export function renderPctileTotal(key: 'qPct' | 'vPct' | 'cPct', wa: { value: number | null; coverage: number }) {
  const axisLabel = key.replace('Pct', '').toUpperCase();
  return (
    <div
      key={key}
      className="col-span-1 text-right tabular-nums text-[9px]"
      style={{ color: pctColor(wa.value) }}
      title={wa.value == null ? 'No history' : `Weighted-avg ${axisLabel} percentile across the book. Coverage: ${Math.round(wa.coverage * 100)}% of weight.`}
    >
      {wa.value == null ? '—' : ordinal(Math.round(wa.value))}
    </div>
  );
}

/** Per-axis "vs own history" percentile cell. Shared between data rows so
 *  Q% / V% / C% can each sit beside its own raw score. */
export function renderPctileCell(axis: 'q' | 'v' | 'c', selfPctile: HoldingSelfPctile) {
  const p = selfPctile[axis];
  const regime = (axis === 'q' ? selfPctile.qRegime : axis === 'v' ? selfPctile.vRegime : selfPctile.cRegime) ?? '';
  const drift = regime.startsWith('model regime shift');
  const titleAxis = axis === 'q' ? 'Q' : axis === 'v' ? 'V' : 'composite';
  const display = p == null ? (drift ? 'drift' : '—') : ordinal(Math.round(p));
  const tip = p != null
    ? `${titleAxis}_self: ${ordinal(Math.round(p))} percentile of this stock's own multi-year history. Higher = better-than-usual on this axis.`
    : drift
    ? `${titleAxis}_self unavailable: model regime shift. Today's value sits >4.5σ from the historical mean — the historical archive was computed under an earlier model version (factor list / pillar weights / formula changed) and is no longer a fair comparison surface for this axis.`
    : `${titleAxis}_self: no history`;
  return (
    <div
      key={`pct-${axis}`}
      className={`col-span-1 text-right tabular-nums text-[9px] font-semibold ${drift ? 'italic' : ''}`}
      style={{ color: drift ? '#94a3b8' : pctColor(p) }}
      title={tip}
    >
      {display}
    </div>
  );
}

/** Weighted-average P&L cell — green/red signed-percent rendering. */
export function PnlPortfolioCell({ wa }: { wa: { value: number | null; coverage: number } }) {
  if (wa.value == null) {
    return <div className="col-span-1 text-right text-[#475569]">—</div>;
  }
  const v = wa.value;
  const color = v > 0 ? 'text-emerald-400' : v < 0 ? 'text-red-400' : 'text-[#94a3b8]';
  const opacity = wa.coverage < 0.8 ? 'opacity-60' : '';
  const sign = v >= 0 ? '+' : '';
  return (
    <div className={`col-span-1 text-right tabular-nums ${color} ${opacity}`} title={`${(wa.coverage * 100).toFixed(0)}% of book has data`}>
      {`${sign}${(v * 100).toFixed(1)}%`}
    </div>
  );
}
